import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { updateImagePosition, updateImageSize } from '../../redux/TabTile/TabActions';
import { RootState } from '../../redux';
import { Rnd } from 'react-rnd';
import './ToolsBar.css';
import './DashBoard.css';
import { AcceptRejectDialog } from '../CommonFunctions/DialogComponents';
import { acceptBtnStyle, rejectBtnStyle } from './ButtonDialogStyles';

interface DraggableImageProps {
  id: string;
  src: string;
  x: number;
  y: number;
  width: number;
  height: number;
  onDragStop: (e: any, d: any, gridSizeX: number, gridSizeY: number) => void;
  onResizeStop: (ref: any, position: any, gridSizeX: number, gridSizeY: number) => void;
  onDelete: (id: string) => void;
  onClick: () => void;
  softUI: boolean;
  backgroundColor: string;
  colorScheme?: string;
  scaling?: string;
  backgroundAlpha?: number;
  borderColor?: string;
  borderRadius?: number;
  borderWidth?: number;
  dashMode: string;
  imageAlpha?: number;
  dashboardHeight?: number;
  dashboardWidth?: number;
}

const DraggableImage: React.FC<DraggableImageProps> = ({
  id,
  src,
  x,
  y,
  width,
  height,
  onDragStop,
  onResizeStop,
  onDelete,
  onClick,
  softUI,
  backgroundColor,
  colorScheme = 'peacock',
  scaling = 'normal',
  backgroundAlpha = 100,
  borderColor = 'transparent',
  borderWidth = 0,
  borderRadius = 12,
  imageAlpha = 100,
  dashMode,
  dashboardHeight,
  dashboardWidth,
}) => {
  //  const dispatch = useDispatch();
  const tabTileProps = useSelector((state: RootState) => state.tabTileProps);
  const { dashGridSize } = tabTileProps;
  const gridSizeX = dashGridSize.x;
  const gridSizeY = dashGridSize.y;
  //  const currentTabId = tabTileProps.selectedTabId;
  const [isHovered, setIsHovered] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const rndRef = useRef<any>(null);

  const constrainPosition = useCallback(
    (currentX: number, currentY: number, currentWidth: number, currentHeight: number) => {
      const effectiveHeight = dashboardHeight || document.body.getBoundingClientRect().height;
      const effectiveWidth = dashboardWidth || document.body.getBoundingClientRect().width;

      const newWidth = currentWidth * gridSizeX;
      const newHeight = currentHeight * gridSizeY;

      // Ensure element stays within dashboard bounds
      const maxX = Math.max(0, (effectiveWidth - newWidth) / gridSizeX);
      const maxY = Math.max(0, (effectiveHeight - newHeight) / gridSizeY);

      const newX = Math.min(Math.max(0, currentX), maxX);
      const newY = Math.min(Math.max(0, currentY), maxY);

      return { x: newX, y: newY };
    },
    [dashboardHeight, dashboardWidth, gridSizeX, gridSizeY],
  );

  const handleDragStop = (e: any, d: any) => {
    const constrainedPos = constrainPosition(d.x / gridSizeX, d.y / gridSizeY, width, height);
    onDragStop(
      e,
      { x: constrainedPos.x * gridSizeX, y: constrainedPos.y * gridSizeY },
      gridSizeX,
      gridSizeY,
    );
  };

  const handleResizeStop = (e: any, direction: any, ref: any, delta: any, newPos: any) => {
    const newWidth = parseInt(ref.style.width, 10);
    const newHeight = parseInt(ref.style.height, 10);

    const constrainedPos = constrainPosition(
      newPos.x / gridSizeX,
      newPos.y / gridSizeY,
      newWidth / gridSizeX,
      newHeight / gridSizeY,
    );

    onResizeStop(
      ref,
      {
        width: newWidth,
        height: newHeight,
        x: constrainedPos.x * gridSizeX,
        y: constrainedPos.y * gridSizeY,
      },
      gridSizeX,
      gridSizeY,
    );
  };

  const getBoxShadow = () => {
    if (!softUI) return 'none';
    switch (colorScheme) {
      case 'dark':
        return '0 0 20px rgba(0,0,0,0.6)';
      case 'chalk':
        return '0 0 18px rgba(0,0,0,0.4)';
      case 'halloween':
        return '0 0 15px rgba(0,0,0,0.3)';
      case 'purplePassion':
        return '0 0 20px rgba(0,0,0,0.5)';
      default:
        return '0 0 15px rgba(0,0,0,0.12)';
    }
  };

  const getRGBA = (hex: string, alphaPercent: number): string => {
    const alpha = Math.min(Math.max(alphaPercent, 0), 100) / 100;
    if (!hex || hex.length < 7 || !/^#([A-Fa-f0-9]{6})$/.test(hex)) {
      return `rgba(0,0,0,0)`;
    }
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const containerStyle: React.CSSProperties = {
    backgroundColor: getRGBA(backgroundColor, backgroundAlpha),
    borderRadius: `${borderRadius ?? 12}px`,
    boxShadow: isHovered ? 'none' : getBoxShadow(),
    overflow: 'hidden',
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: isHovered ? '1px solid gray' : `${borderWidth}px solid ${borderColor}`,
    transition: 'border 0.2s ease, box-shadow 0.2s ease',
    position: 'relative',
    outlineOffset: '-2px',
  };

  const imageStyle: React.CSSProperties = {
    width: '100%',
    height: '100%',
    objectFit: scaling as React.CSSProperties['objectFit'],
    opacity: imageAlpha / 100,
    // borderRadius: borderRadius > 0 ? `${borderRadius}px` : softUI ? '12px' : '0px',
    pointerEvents: 'none',
  };

  const validX = isNaN(x) ? gridSizeX : x;
  const validY = isNaN(y) ? 0 : y;

  return (
    <Rnd
      ref={rndRef}
      size={{ width: width * gridSizeX, height: height * gridSizeY }}
      position={{ x: validX * gridSizeX, y: validY * gridSizeY }}
      onDragStop={handleDragStop}
      onResizeStop={handleResizeStop}
      bounds="parent"
      enableResizing={dashMode === 'Edit'}
      minWidth={100}
      minHeight={100}
      dragGrid={[gridSizeX, gridSizeY]}
      resizeGrid={[gridSizeX, gridSizeY]}
      className="dashboard-image"
      onClick={onClick}
      disableDragging={dashMode !== 'Edit'}
      style={{ zIndex: 3 }}
    >
      <div
        style={containerStyle}
        onMouseEnter={() => dashMode === 'Edit' && setIsHovered(true)}
        onMouseLeave={() => dashMode === 'Edit' && setIsHovered(false)}
      >
        <img src={src} alt="" style={imageStyle} />
        {isHovered && dashMode === 'Edit' && (
          <div className="DraggableImage-delete-button-container">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setConfirmOpen(true);
              }}
            >
              ×
            </button>
          </div>
        )}
        <AcceptRejectDialog
          open={confirmOpen}
          heading={`Remove Image`}
          messages={[{ text: `Are you sure you want to remove this Image from the dashboard?` }]}
          acceptText="Remove"
          rejectText="Cancel"
          acceptButtonStyle={acceptBtnStyle}
          rejectButtonStyle={rejectBtnStyle}
          acceptFunction={() => {
            onDelete(id);
            setConfirmOpen(false);
          }}
          rejectFunction={() => setConfirmOpen(false)}
          closeFunction={() => setConfirmOpen(false)}
        />
      </div>
    </Rnd>
  );
};

export default DraggableImage;
